import {openConfirmDialog} from 'platform/components';

import {useCallback} from 'react';

import i18n from '@dms/i18n';

import {VEHICLE_FORM_EVENTS} from '../../core/machine/constants/VEHICLE_FORM_EVENTS';
import {useVehicleFormContext} from './useVehicleFormContext';

/**
 * Hook for creating custom vehicle makes and models
 * Provides functions to handle user-defined makes and models with confirmation dialogs
 *
 * @returns Object containing functions to create custom makes and models
 *
 * @example
 * ```tsx
 * function MyVehicleForm() {
 *   const {createCustomMake, createCustomModel} = useVehicleCreateMakeModel();
 *
 *   return (
 *     <VehicleForm.MakeSelect
 *       onCreateOption={createCustomMake}
 *     />
 *   );
 * }
 * ```
 */
export function useVehicleCreateMakeModel() {
  const {vehicleType, baseTestId, sendMachineEvent, formApi} = useVehicleFormContext();

  /**
   * Creates a custom vehicle make with user confirmation
   * Sends event to XState machine to handle form updates and field resets
   */
  const createCustomMake = useCallback(
    (customMake: string) => {
      if (!vehicleType) {
        console.warn('Cannot create custom make: vehicle type not selected');
        return;
      }

      openConfirmDialog({
        text: i18n.t('entity.vehicle.labels.createCustomMakeConfirm', {make: customMake}),
        onConfirm: () => {
          // Send event to XState machine to handle form updates
          sendMachineEvent({
            type: VEHICLE_FORM_EVENTS.CREATE_CUSTOM_MAKE,
            customMake,
          });
        },
        'data-testid': `create-custom-make-confirm-dialog-${baseTestId}`,
      });
    },
    [vehicleType, sendMachineEvent, baseTestId]
  );

  /**
   * Creates a custom vehicle model with user confirmation
   * Sends event to XState machine to handle form updates and field resets
   */
  const createCustomModel = useCallback(
    (customModel: string) => {
      if (!vehicleType) {
        console.warn('Cannot create custom model: vehicle type not selected');
        return;
      }

      const currentMake = formApi.getValues('formMake');
      if (!currentMake) {
        console.warn('Cannot create custom model: make not selected');
        return;
      }

      openConfirmDialog({
        text: i18n.t('entity.vehicle.labels.createCustomModelConfirm', {
          model: customModel,
        }),
        onConfirm: () => {
          sendMachineEvent({
            type: VEHICLE_FORM_EVENTS.CREATE_CUSTOM_MODEL,
            customModel,
          });
        },
        'data-testid': `create-custom-model-confirm-dialog-${baseTestId}`,
      });
    },
    [vehicleType, formApi, sendMachineEvent, baseTestId]
  );

  return {
    createCustomMake,
    createCustomModel,
  };
}
